import Links from '@/shared/constants/navegacao';
import {
  colunasGeral,
  colunasDefensor,
  colunasEstagiarios,
  colunasUsuarios,
  colunasColaboradores,
} from './index_columns';
import { useCallback, useEffect, useMemo } from 'react';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { PeopleDataTable } from '../components/PeopleDataTable';
import { useSearchParams } from 'react-router-dom';

export type TipoPessoaIndex = 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario';

const SEARCH_PARAM_TIPO = 'tipo';

const TIPO_PESSOA_OPCOES: { value: TipoPessoaIndex; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'defensor', label: 'Defensores' },
  { value: 'estagiario', label: 'Estagiários' },
  { value: 'colaborador', label: 'Colaboradores' },
  { value: 'usuario', label: 'Usuários' },
];

const TITULOS_POR_TIPO: Record<TipoPessoaIndex, string> = {
  todos: 'Cadastros de pessoas',
  defensor: 'Defensores',
  estagiario: 'Estagiários',
  colaborador: 'Colaboradores',
  usuario: 'Usuários',
};

function resolveTipoFromSearchParams(tipoParam: string | null): TipoPessoaIndex {
  if (
    tipoParam === 'defensor' ||
    tipoParam === 'estagiario' ||
    tipoParam === 'colaborador' ||
    tipoParam === 'usuario'
  ) {
    return tipoParam;
  }
  return 'todos';
}

export default function PersonIndexPage() {
  const { setActions } = useBreadcrumbActions();
  const [searchParams, setSearchParams] = useSearchParams();

  const tipo = useMemo(
    () => resolveTipoFromSearchParams(searchParams.get(SEARCH_PARAM_TIPO)),
    [searchParams],
  );

  const { titulo, colunas } = useMemo(() => {
    switch (tipo) {
      case 'colaborador':
        return {
          titulo: TITULOS_POR_TIPO.colaborador,
          colunas: colunasColaboradores,
        };
      case 'defensor':
        return {
          titulo: TITULOS_POR_TIPO.defensor,
          colunas: colunasDefensor,
        };
      case 'estagiario':
        return {
          titulo: TITULOS_POR_TIPO.estagiario,
          colunas: colunasEstagiarios,
        };
      case 'usuario':
        return {
          titulo: TITULOS_POR_TIPO.usuario,
          colunas: colunasUsuarios,
        };
      default:
        return {
          titulo: TITULOS_POR_TIPO.todos,
          colunas: colunasGeral,
        };
    }
  }, [tipo]);

  const createLink = Links.Pessoa_Criar;

  const handleTrocarTipo = useCallback(
    (novoTipo: TipoPessoaIndex) => {
      if (novoTipo === tipo) return;

      const novosParams = new URLSearchParams(searchParams);
      if (novoTipo === 'todos') {
        novosParams.delete(SEARCH_PARAM_TIPO);
      } else {
        novosParams.set(SEARCH_PARAM_TIPO, novoTipo);
      }
      setSearchParams(novosParams);
    },
    [searchParams, setSearchParams, tipo],
  );

  useEffect(() => {
    const config = {
      primary: { label: 'Cadastrar', onClick: () => { }, href: createLink },
      secondary: { label: 'Aniversários', onClick: () => { }, href: Links.Pessoa_Aniversarios },
      tertiary: { label: 'Assessores', onClick: () => { }, href: Links.Pessoa_Assessores },
    };
    setActions(config);
    return () => setActions({});
  }, [setActions, createLink, tipo]);

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
          <h3 className="mb-0">{titulo}</h3>
          <div className="btn-group btn-group-sm" role="group" aria-label="Perfil de pessoa">
            {TIPO_PESSOA_OPCOES.map((opcao) => (
              <button
                key={opcao.value}
                type="button"
                className={`btn ${tipo === opcao.value ? 'btn-success' : 'btn-outline-success'}`}
                aria-pressed={tipo === opcao.value}
                onClick={() => handleTrocarTipo(opcao.value)}
              >
                {opcao.label}
              </button>
            ))}
          </div>
        </div>
        <span className="blockquote-footer pb-4 text-end">
          {titulo} cadastrados no sistema.
        </span>
      </div>

      <hr className="pb-4" />

      <PeopleDataTable tipo={tipo} columns={colunas} />
    </div>
  );
}
